import { useCallback, useEffect } from 'preact/hooks';

import appEvents from '@/shared/appEvents';

import { OnAppEventConfig } from '../types';

interface TourAppEventSlotProps {
  config: OnAppEventConfig;
  onFire: (config: OnAppEventConfig) => void;
}

/**
 * Headless: advances the tour when the user actually performs an action, rather than clicking a
 * banner button. "Now zoom in" is answered by zooming.
 *
 * One instance per listener, so a step can wait on several actions at once and send each
 * somewhere different. It takes its own config rather than a pre-bound callback so the handler
 * identity depends only on that config — an inline closure from the parent would change every
 * render and detach/re-attach the subscription with it.
 *
 * Mounted only while its config resolves, so a listener whose `when` or `cond` does not match is
 * not subscribed at all — being off-path, or mid-`pending`, cannot advance the user by accident.
 *
 * Deliberately has no memory: an event fired before this mounts is not replayed. That is right
 * for onboarding, where the step is *asking* the user to do the thing; someone who zoomed a
 * minute ago should still be shown the zoom step.
 *
 * Habitat-scoped correctness belongs at the emit site (e.g. clearing a draft on habitat switch
 * must fire `habitat:media-discarded`), not here — this slot stays tour-agnostic.
 */
const TourAppEventSlot = ({ config, onFire }: TourAppEventSlotProps) => {
  const handler = useCallback(() => onFire(config), [onFire, config]);

  useEffect(() => {
    appEvents.on(config.event, handler);
    return () => {
      appEvents.off(config.event, handler);
    };
  }, [config.event, handler]);

  return null;
};

export default TourAppEventSlot;
